import React, { useEffect, useState, useCallback } from 'react';
import {
  ApplySubmission, getApplySubmissions, markApplySubmission, deleteApplySubmission,
  ContactMessage, getContactMessages, markContactMessage, deleteContactMessage,
} from './api';

/* Boîte de réception : candidatures (Postuler TikTokeur / Agent) + messages
   de contact (vitrine). Bouton enveloppe fixe, panneau avec 2 onglets. */

function fmt(iso: string): string {
  const d = new Date((iso || '').replace(' ', 'T'));
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

type Tab = 'apply' | 'messages';

export default function ApplyInbox({ inline = false }: { inline?: boolean }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('apply');
  const [items, setItems] = useState<ApplySubmission[]>([]);
  const [msgs, setMsgs] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, m] = await Promise.all([
        getApplySubmissions().then(r => r.data).catch(() => [] as ApplySubmission[]),
        getContactMessages().then(r => r.data).catch(() => [] as ContactMessage[]),
      ]);
      setItems(a);
      setMsgs(m);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const unreadApply = items.filter(i => !i.is_read).length;
  const unreadMsg = msgs.filter(i => !i.is_read).length;
  const unread = unreadApply + unreadMsg;

  const toggleReadApply = async (it: ApplySubmission) => {
    const next = !it.is_read;
    setItems(p => p.map(x => x.id === it.id ? { ...x, is_read: next ? 1 : 0 } : x));
    try { await markApplySubmission(it.id, next); } catch { load(); }
  };
  const removeApply = async (it: ApplySubmission) => {
    if (!window.confirm('Supprimer cette candidature ?')) return;
    setItems(p => p.filter(x => x.id !== it.id));
    try { await deleteApplySubmission(it.id); } catch { load(); }
  };
  const toggleReadMsg = async (it: ContactMessage) => {
    const next = !it.is_read;
    setMsgs(p => p.map(x => x.id === it.id ? { ...x, is_read: next ? 1 : 0 } : x));
    try { await markContactMessage(it.id, next); } catch { load(); }
  };
  const removeMsg = async (it: ContactMessage) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    setMsgs(p => p.filter(x => x.id !== it.id));
    try { await deleteContactMessage(it.id); } catch { load(); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} title="Candidatures & messages"
        style={inline
          ? { position: 'relative', width: 42, height: 42, borderRadius: 10, border: 'none', cursor: 'pointer', background: '#0f172a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }
          : { position: 'fixed', top: 16, right: 74, zIndex: 1000, width: 46, height: 46, borderRadius: 23, border: 'none', cursor: 'pointer', background: '#0f172a', color: '#fff', boxShadow: '0 4px 14px rgba(15,23,42,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" />
        </svg>
        {unread > 0 && (
          <span style={{ position: 'absolute', top: -4, right: -4, minWidth: 18, height: 18, padding: '0 4px', borderRadius: 9, background: '#dc2626', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{unread}</span>
        )}
      </button>
    );
  }

  const tabBtn = (id: Tab, label: string, n: number): React.CSSProperties => ({
    flex: 1, padding: '9px 6px', border: 'none', cursor: 'pointer',
    background: tab === id ? '#fff' : '#f1f5f9',
    color: tab === id ? '#7c3aed' : '#64748b',
    fontWeight: 700, fontSize: 13,
    borderBottom: tab === id ? '2px solid #7c3aed' : '2px solid transparent',
  });

  return (
    <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(440px,100vw)', zIndex: 1000, background: '#fff', borderLeft: '1px solid #e2e8f0', boxShadow: '-8px 0 24px rgba(15,23,42,0.12)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flexShrink: 0, padding: '12px 14px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 10 }}>
        <strong style={{ fontSize: 14, color: '#0f172a', flex: 1 }}>✉️ Boîte de réception</strong>
        <button onClick={load} title="Rafraîchir" style={{ border: '1px solid #e2e8f0', background: '#f8fafc', borderRadius: 6, fontSize: 12, padding: '4px 8px', cursor: 'pointer', color: '#374151' }}>↻</button>
        <button onClick={() => setOpen(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 18, color: '#64748b', lineHeight: 1 }}>×</button>
      </div>

      <div style={{ flexShrink: 0, display: 'flex', borderBottom: '1px solid #e2e8f0' }}>
        <button style={tabBtn('apply', 'Candidatures', unreadApply)} onClick={() => setTab('apply')}>
          Candidatures{unreadApply > 0 && <span style={{ marginLeft: 6, background: '#dc2626', color: '#fff', borderRadius: 9, padding: '0 6px', fontSize: 11 }}>{unreadApply}</span>}
        </button>
        <button style={tabBtn('messages', 'Messages', unreadMsg)} onClick={() => setTab('messages')}>
          Messages{unreadMsg > 0 && <span style={{ marginLeft: 6, background: '#dc2626', color: '#fff', borderRadius: 9, padding: '0 6px', fontSize: 11 }}>{unreadMsg}</span>}
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 12, background: '#f8fafc' }}>
        {loading && items.length === 0 && msgs.length === 0 && (
          <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', marginTop: 30 }}>Chargement…</div>
        )}

        {tab === 'apply' && (
          <>
            {!loading && items.length === 0 && (
              <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', marginTop: 30 }}>Aucune candidature pour le moment.</div>
            )}
            {items.map(it => (
              <div key={it.id} style={{ background: '#fff', border: '1px solid ' + (it.is_read ? '#e2e8f0' : '#7c3aed'), borderRadius: 10, padding: 12, fontSize: 13 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, color: '#fff', background: it.form === 'tiktokeur' ? '#7c3aed' : '#0891b2' }}>
                    {it.form === 'tiktokeur' ? '🎬 TikTokeur' : '🧑‍💼 Agent'}
                  </span>
                  {!it.is_read && <span style={{ fontSize: 10, fontWeight: 700, color: '#dc2626' }}>● NOUVEAU</span>}
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8' }}>{fmt(it.created_at)}</span>
                </div>
                <div style={{ fontWeight: 700, color: '#0f172a' }}>{it.pseudo || '—'}</div>
                {it.tiktok_link && (
                  <a href={it.tiktok_link} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#7c3aed', wordBreak: 'break-all' }}>{it.tiktok_link}</a>
                )}
                {it.concept && (
                  <div style={{ marginTop: 6, color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{it.concept}</div>
                )}
                <div style={{ marginTop: 6, fontSize: 11, color: it.certified_adult ? '#16a34a' : '#dc2626' }}>
                  {it.certified_adult ? '✓ Majeur(e) certifié' : '⚠ Non certifié majeur'}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button onClick={() => toggleReadApply(it)} style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#374151', cursor: 'pointer' }}>
                    {it.is_read ? 'Marquer non lu' : 'Marquer lu'}
                  </button>
                  <button onClick={() => removeApply(it)} style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, border: '1px solid #fecaca', background: '#fff', color: '#dc2626', cursor: 'pointer' }}>
                    Supprimer
                  </button>
                </div>
              </div>
            ))}
          </>
        )}

        {tab === 'messages' && (
          <>
            {!loading && msgs.length === 0 && (
              <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', marginTop: 30 }}>Aucun message pour le moment.</div>
            )}
            {msgs.map(it => (
              <div key={it.id} style={{ background: '#fff', border: '1px solid ' + (it.is_read ? '#e2e8f0' : '#0891b2'), borderRadius: 10, padding: 12, fontSize: 13 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, color: '#fff', background: '#0891b2' }}>✉️ Message</span>
                  {!it.is_read && <span style={{ fontSize: 10, fontWeight: 700, color: '#dc2626' }}>● NOUVEAU</span>}
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8' }}>{fmt(it.created_at)}</span>
                </div>
                {it.name && <div style={{ fontWeight: 700, color: '#0f172a' }}>{it.name}</div>}
                {it.tiktok && (
                  <a href={`https://www.tiktok.com/@${(it.tiktok || '').replace(/^@/, '')}`} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#7c3aed', wordBreak: 'break-all', display: 'block' }}>
                    @{(it.tiktok || '').replace(/^@/, '')}
                  </a>
                )}
                {it.email && (
                  <a href={`mailto:${it.email}`} style={{ fontSize: 12, color: '#0891b2', wordBreak: 'break-all', display: 'block' }}>{it.email}</a>
                )}
                <div style={{ marginTop: 6, color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{it.message}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button onClick={() => toggleReadMsg(it)} style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#374151', cursor: 'pointer' }}>
                    {it.is_read ? 'Marquer non lu' : 'Marquer lu'}
                  </button>
                  <button onClick={() => removeMsg(it)} style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, border: '1px solid #fecaca', background: '#fff', color: '#dc2626', cursor: 'pointer' }}>
                    Supprimer
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
